import React, {useState, useEffect} from "react";
import {useSeo} from "../hooks/useSeo";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  ButtonGroup,
  Table,
  Badge,
  Alert,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {formatDate} from "../utils/dates";
import {Line} from "react-chartjs-2";
import {Chart, ChartData, registerables} from "chart.js";
import _ from "lodash";
import {usePostHog} from "@posthog/react";

import {FeaturedProps} from "../containers/Featured";
import {CardSkeleton} from "./SkeletonLoader";
import {getTickerType, TickerType} from "../utils/typeValidation";
import {buildViewFeaturedUrl, SIGNUP_URL} from "../utils/links";
import {useAuthUrl} from "../utils/authRedirect";
import ShareButton from "./ShareButton";
import {FeaturedFilters} from "../actions/feature";

// Register ChartJS components
Chart.register(...registerables);

// Card-level warning badge when more than this share of a signal's analog matches are
// delisted securities that no longer trade (backend: fraction_delisted, 0-100 scale) —
// so a heavily-delisted-analog signal is visible before a user clicks into the detail
// view, not just discoverable by counting rows there.
const DELISTED_BADGE_THRESHOLD = 50;

const rsiToFilters = (rsi: "all" | "oversold" | "overbought"): FeaturedFilters | undefined => {
  if (rsi === "oversold") return {rsiMax: 30};
  if (rsi === "overbought") return {rsiMin: 70};
  return undefined;
};

const Featured = ({content, getFeaturedMatches}: FeaturedProps) => {
  const authUrlFor = useAuthUrl();
  useSeo({
    title: "Featured Matches — Today's Charts with Close Historical Analogs",
    description: "DejaChart's daily featured matches: stocks whose current chart most closely mirrors historical analogs, ranked by directional conviction across 2-week to 1-year windows.",
    path: "/featured",
  });
  const posthog = usePostHog();
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [viewMode, setViewMode] = useState(() =>
    window.innerWidth >= 768 ? "table" : "cards"
  ); // 'cards' or 'table'
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [directionFilter, setDirectionFilter] = useState("all"); // 'all', 'up', 'down'
  const [windowFilter, setWindowFilter] = useState("20"); // '10', '20', '65', '130', '260'
  const [tickerTypeFilter, setTickerTypeFilter] = useState<TickerType | 'all'>('all');
  const [showScoreColumns, setShowScoreColumns] = useState(false);
  // Min conviction / sort are computed client-side over the already-fetched content,
  // same as every other filter on this page — the fields they need (fraction_up,
  // avg_up/avg_dn, count_up/count_dn) are already in the payload.
  const [minConviction, setMinConviction] = useState(0);
  const [sortBy, setSortBy] = useState<"default" | "conviction" | "count" | "avg_move">("default");
  // RSI genuinely isn't in the payload — this one has to be a server-side refetch.
  const [rsiFilter, setRsiFilter] = useState<"all" | "oversold" | "overbought">("all");

  const captureMatchClick = (stock: StockData) => {
    posthog.capture('featured_match_clicked', {
      ticker: stock.query.ticker,
      direction: stock.direction,
      window: stock.window,
      fraction_up: stock.fraction_up,
      avg_up: stock.avg_up,
      avg_dn: stock.avg_dn,
    });
  };

  // Load initial data
  useEffect(() => {
    if (!initialLoadDone && !content) {
      getFeaturedMatches();
      setInitialLoadDone(true);
    } else if (content) {
      setIsLoading(false);
    }
  }, [content, getFeaturedMatches, initialLoadDone]);

  // viewMode's initial value only checks window width once, at mount. Without this, a
  // page loaded wide (tablet, landscape phone) that's then rotated/resized narrower keeps
  // showing the table — but the Cards/Table toggle is CSS-hidden below 768px (assumes
  // mobile is always Cards), so the user gets stuck in a table with most columns scrolled
  // off-screen and no control to switch back.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode((v) => (v === "table" ? "cards" : v));
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setIsLoading(true);
    getFeaturedMatches(date, undefined, rsiToFilters(rsiFilter)).then(() => {
      setIsLoading(false);
    });
    setDropdownOpen(false);
  };

  // RSI isn't in the featured payload (unlike conviction/count/avg move), so this is
  // the one control that has to trigger a real refetch against /api/featured's new
  // rsi_min/rsi_max params instead of filtering client-side.
  const handleRsiSelect = (next: "all" | "oversold" | "overbought") => {
    setRsiFilter(next);
    setIsLoading(true);
    getFeaturedMatches(selectedDate, undefined, rsiToFilters(next)).then(() => {
      setIsLoading(false);
    });
  };


  // Get the active window filter - if current value has no data, return next available
  const getActiveWindowFilter = (): string => {
    if (!content) return windowFilter;

    const windowOrder = ["10", "20", "65", "130", "260"];
    const contentMap: {[key: string]: boolean} = {
      "10": !!content.featured10,
      "20": !!content.featured20,
      "65": !!content.featured65,
      "130": !!content.featured130,
      "260": !!content.featured260,
    };

    // If current windowFilter has data, return it
    if (contentMap[windowFilter]) {
      return windowFilter;
    }

    // Otherwise, find and return the next available window
    const currentIndex = windowOrder.indexOf(windowFilter);
    for (let i = currentIndex + 1; i < windowOrder.length; i++) {
      if (contentMap[windowOrder[i]]) {
        return windowOrder[i];
      }
    }

    // If no window after current is available, find the first available one before it
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (contentMap[windowOrder[i]]) {
        return windowOrder[i];
      }
    }

    // Fallback to original windowFilter
    return windowFilter;
  };

  // Function to create chart data from stock data
  const createChartData = (stockData: StockData) => {
    const prices = stockData.query.query_data;
    const labels = stockData.query.query_dates.map((date) => {
      const d = new Date(date);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    });

    const isUptrend = stockData.fraction_up > 50;

    return {
      labels,
      datasets: [
        {
          data: prices,
          borderColor: isUptrend ? "#059669" : "#DC2626",
          backgroundColor: isUptrend
            ? "rgba(5, 150, 105, 0.1)"
            : "rgba(220, 38, 38, 0.1)",
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          borderWidth: 2,
        },
      ],
    } as ChartData<"line", number[], string>;
  };

  // Chart options
  const miniChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        radius: 0,
      },
    },
  };

  // Display date dropdown component
  const DisplayDate = () => {
    if (_.isUndefined(content)) return null;

    // featured_dates is typed non-null, but a 200 with a partial body omits it.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const reversedDates = [...(content.featured_dates ?? [])].reverse();
    // Use selectedDate if set, otherwise use most recent available date
    const featureDate = selectedDate || reversedDates[0];

    return (
      <div className="d-flex align-items-center mb-3">
        <h5 className="me-2 mb-0">Pattern End Date:</h5>
        <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)}>
          <DropdownToggle color="primary" outline caret>
            {featureDate}
          </DropdownToggle>
          <DropdownMenu>
            {reversedDates.map((date) => (
              <DropdownItem
                key={date}
                active={date === featureDate}
                onClick={() => handleDateSelect(date)}
              >
                {date}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>

        <div className="ms-auto view-mode-toggle">
          <ButtonGroup>
            <Button
              color={viewMode === "cards" ? "primary" : "light"}
              onClick={() => setViewMode("cards")}
            >
              <i className="fas fa-th"></i> Cards
            </Button>
            <Button
              color={viewMode === "table" ? "primary" : "light"}
              onClick={() => setViewMode("table")}
            >
              <i className="fas fa-table"></i> Table
            </Button>
          </ButtonGroup>
        </div>
      </div>
    );
  };

  // Filter Controls
  const FilterControls = () => {
    const activeWindow = getActiveWindowFilter();

    return (
      <div className="mb-3 filter-controls">
        <div className="filter-section mb-2">
          {/*<h6 className="filter-label">Direction:</h6>*/}
          <div className="d-flex flex-wrap filter-buttons">
            <Button
              color={directionFilter === "all" ? "primary" : "light"}
              className="me-2 mb-2"
              onClick={() => setDirectionFilter("all")}
            >
              All Directions
            </Button>
            <Button
              color={directionFilter === "up" ? "success" : "light"}
              className="me-2 mb-2"
              onClick={() => setDirectionFilter("up")}
            >
              Bullish
            </Button>
            <Button
              color={directionFilter === "down" ? "danger" : "light"}
              className="mb-2"
              onClick={() => setDirectionFilter("down")}
            >
              Bearish
            </Button>
          </div>
        </div>

        <div className="filter-section">
          <h6 className="filter-label text-muted small mb-1">Pattern Length</h6>
          <div className="d-flex flex-wrap filter-buttons">
            {(() => {
              const windowData: {[key: string]: FeaturedStockData | null | undefined} = {
                "10": content?.featured10,
                "20": content?.featured20,
                "65": content?.featured65,
                "130": content?.featured130,
                "260": content?.featured260,
              };
              const windows = [
                {key: "10", label: "2 weeks"},
                {key: "20", label: "1 month"},
                {key: "65", label: "3 months"},
                {key: "130", label: "6 months"},
                {key: "260", label: "1 year"},
              ];
              return windows.map(({key, label}) => {
                if (windowData[key]) {
                  return (
                    <Button
                      key={key}
                      color={activeWindow === key ? "primary" : "light"}
                      className="me-2 mb-2"
                      onClick={() => setWindowFilter(key)}
                    >
                      {label}
                    </Button>
                  );
                }
                // No window is Pro-locked since 2026-08-11; a missing section just means
                // the pkl hasn't been generated yet → hide (original behavior).
                return null;
              });
            })()}
          </div>
        </div>

        <div className="filter-section">
          <h6 className="filter-label text-muted small mb-1">Asset Type</h6>
          <div className="d-flex flex-wrap filter-buttons">
            <Button
              color={tickerTypeFilter === "all" ? "primary" : "light"}
              className="me-2 mb-2"
              onClick={() => setTickerTypeFilter("all")}
            >
              All
            </Button>
            <Button
              color={tickerTypeFilter === "stock" ? "primary" : "light"}
              className="me-2 mb-2"
              onClick={() => setTickerTypeFilter("stock")}
            >
              Stocks/ETFs
            </Button>
            <Button
              color={tickerTypeFilter === "index" ? "primary" : "light"}
              className="me-2 mb-2"
              onClick={() => setTickerTypeFilter("index")}
            >
              Indexes
            </Button>
            <Button
              color={tickerTypeFilter === "crypto" ? "primary" : "light"}
              className="me-2 mb-2"
              onClick={() => setTickerTypeFilter("crypto")}
            >
              Crypto & Forex
            </Button>
            <Button
              color={tickerTypeFilter === "futures" ? "primary" : "light"}
              className="mb-2"
              onClick={() => setTickerTypeFilter("futures")}
            >
              Futures
            </Button>
          </div>
        </div>

        <div className="filter-section">
          <h6 className="filter-label text-muted small mb-1">
            Min Conviction {minConviction > 0 ? `(${minConviction}%+)` : ""}
          </h6>
          <input
            type="range"
            min={0}
            max={95}
            step={5}
            value={minConviction}
            onChange={(e) => setMinConviction(Number(e.target.value))}
            className="form-range"
            style={{maxWidth: "260px"}}
            aria-label="Minimum conviction percentage"
          />
        </div>

        <div className="filter-section">
          <h6 className="filter-label text-muted small mb-1">Sort By</h6>
          <div className="d-flex flex-wrap filter-buttons">
            <Button
              color={sortBy === "default" ? "primary" : "light"}
              className="me-2 mb-2"
              onClick={() => setSortBy("default")}
            >
              Default
            </Button>
            <Button
              color={sortBy === "conviction" ? "primary" : "light"}
              className="me-2 mb-2"
              onClick={() => setSortBy("conviction")}
            >
              Conviction
            </Button>
            <Button
              color={sortBy === "count" ? "primary" : "light"}
              className="me-2 mb-2"
              onClick={() => setSortBy("count")}
            >
              Analog Count
            </Button>
            <Button
              color={sortBy === "avg_move" ? "primary" : "light"}
              className="mb-2"
              onClick={() => setSortBy("avg_move")}
            >
              Avg Move
            </Button>
          </div>
        </div>

        <div className="filter-section">
          <h6 className="filter-label text-muted small mb-1">RSI (14)</h6>
          <div className="d-flex flex-wrap filter-buttons">
            <Button
              color={rsiFilter === "all" ? "primary" : "light"}
              className="me-2 mb-2"
              onClick={() => handleRsiSelect("all")}
            >
              All
            </Button>
            <Button
              color={rsiFilter === "oversold" ? "primary" : "light"}
              className="me-2 mb-2"
              onClick={() => handleRsiSelect("oversold")}
            >
              Oversold (≤30)
            </Button>
            <Button
              color={rsiFilter === "overbought" ? "primary" : "light"}
              className="mb-2"
              onClick={() => handleRsiSelect("overbought")}
            >
              Overbought (≥70)
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Filter stocks based on selected filters
  const getFilteredStocks = (): StockData[] => {
    if (!content) return [];

    let result: StockData[] = [];
    const activeWindow = getActiveWindowFilter();

    // Collect data based on window filter
    if (activeWindow === "10") {
      if (directionFilter === "all" || directionFilter === "up") {
        result = [
          ...result,
          ...(content.featured10?.up ?? []).map((stock) => ({
            ...stock,
            direction: "up" as const,
            window: 10,
          })),
        ];
      }
      if (directionFilter === "all" || directionFilter === "down") {
        result = [
          ...result,
          ...(content.featured10?.down ?? []).map((stock) => ({
            ...stock,
            direction: "down" as const,
            window: 10,
          })),
        ];
      }
    } else if (activeWindow === "20") {
      if (directionFilter === "all" || directionFilter === "up") {
        result = [
          ...result,
          ...(content.featured20?.up ?? []).map((stock) => ({
            ...stock,
            direction: "up" as const,
            window: 20,
          })),
        ];
      }
      if (directionFilter === "all" || directionFilter === "down") {
        result = [
          ...result,
          ...(content.featured20?.down ?? []).map((stock) => ({
            ...stock,
            direction: "down" as const,
            window: 20,
          })),
        ];
      }
    } else if (activeWindow === "65") {
      if (directionFilter === "all" || directionFilter === "up") {
        result = [
          ...result,
          ...(content.featured65?.up ?? []).map((stock) => ({
            ...stock,
            direction: "up" as const,
            window: 65,
          })),
        ];
      }
      if (directionFilter === "all" || directionFilter === "down") {
        result = [
          ...result,
          ...(content.featured65?.down ?? []).map((stock) => ({
            ...stock,
            direction: "down" as const,
            window: 65,
          })),
        ];
      }
    } else if (activeWindow === "130") {
      if (directionFilter === "all" || directionFilter === "up") {
        result = [
          ...result,
          ...(content.featured130?.up ?? []).map((stock) => ({
            ...stock,
            direction: "up" as const,
            window: 130,
          })),
        ];
      }
      if (directionFilter === "all" || directionFilter === "down") {
        result = [
          ...result,
          ...(content.featured130?.down ?? []).map((stock) => ({
            ...stock,
            direction: "down" as const,
            window: 130,
          })),
        ];
      }
    } else if (activeWindow === "260") {
      if (directionFilter === "all" || directionFilter === "up") {
        result = [
          ...result,
          ...(content.featured260?.up ?? []).map((stock) => ({
            ...stock,
            direction: "up" as const,
            window: 260,
          })),
        ];
      }
      if (directionFilter === "all" || directionFilter === "down") {
        result = [
          ...result,
          ...(content.featured260?.down ?? []).map((stock) => ({
            ...stock,
            direction: "down" as const,
            window: 260,
          })),
        ];
      }
    }

    // Filter by asset type
    if (tickerTypeFilter !== "all") {
      result = result.filter((stock) => getTickerType(stock.query.ticker) === tickerTypeFilter);
    }

    // Min conviction: % of THIS stock's own analogs that agreed with the call —
    // fraction_up as-is for a bullish row, 100-fraction_up for a bearish row
    // (mirrors the backend's _stock_conviction in resources/feature.py).
    if (minConviction > 0) {
      result = result.filter((stock) => {
        const conviction = stock.direction === "up" ? stock.fraction_up : 100 - stock.fraction_up;
        return conviction >= minConviction;
      });
    }

    if (sortBy === "conviction") {
      result.sort((a, b) => {
        const aConv = a.direction === "up" ? a.fraction_up : 100 - a.fraction_up;
        const bConv = b.direction === "up" ? b.fraction_up : 100 - b.fraction_up;
        return bConv - aConv;
      });
    } else if (sortBy === "count") {
      result.sort((a, b) => (b.count_up + b.count_dn) - (a.count_up + a.count_dn));
    } else if (sortBy === "avg_move") {
      result.sort((a, b) => {
        const aMove = a.direction === "up" ? a.avg_up : Math.abs(a.avg_dn);
        const bMove = b.direction === "up" ? b.avg_up : Math.abs(b.avg_dn);
        return bMove - aMove;
      });
    } else if (directionFilter === "all") {
      // Default: sort by total score (considering both up and down)
      result.sort((a, b) => {
        const aScore = a.direction === "up" ? a.total_up_score : a.total_down_score;
        const bScore = b.direction === "up" ? b.total_up_score : b.total_down_score;
        return bScore - aScore;
      });
    } else if (directionFilter === "up") {
      // Default: sort by total up score when showing only bullish
      result.sort((a, b) => b.total_up_score - a.total_up_score);
    } else {
      // Default: sort by total down score when showing only bearish
      result.sort((a, b) => b.total_down_score - a.total_down_score);
    }

    return result;
  };

  // Card view for stock data
  const StockCardView = ({stocks}: {stocks: StockData[]}) => {
    if (stocks.length === 0) {
      return (
        <div className="text-center py-4">
          <p>No matches found for the selected filters.</p>
        </div>
      );
    }

    //stocks.map((stock, i) => console.log(stock));

    return (
      <Row>
        {stocks.map((stock, i) => {
          const isObfuscated = stock.query.ticker === "?????";
          return (
            <Col lg="4" md="6" key={`${stock.query.ticker}_${stock.direction}_${i}`} className="mb-4">
              <Card className="featured-stock-card h-100 border-0 shadow-sm">
                <CardHeader className="bg-white d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-0 d-flex align-items-center">
                      <span style={isObfuscated ? {filter: "blur(6px)", userSelect: "none"} : {}}>
                        {stock.query.ticker}
                      </span>
                      {isObfuscated && <span className="ms-1">🔒</span>}
                      <Badge
                        color={stock.direction === "up" ? "success" : "danger"}
                        className="ms-2"
                      >
                        {stock.direction === "up" ? "BULLISH" : "BEARISH"}
                      </Badge>
                      <Badge color="secondary" className="ms-2">
                        {stock.window}D
                      </Badge>
                      {stock.ma_pending && (
                        <Badge
                          color="info"
                          className="ms-2"
                          title="This stock is currently under a live merger/acquisition agreement — its price may be converging toward a fixed deal price (arbitrage) rather than moving on the pattern shown here"
                        >
                          M&A
                        </Badge>
                      )}
                      {stock.fraction_delisted > DELISTED_BADGE_THRESHOLD && (
                        <Badge
                          color="warning"
                          className="ms-2"
                          title={`${stock.fraction_delisted.toFixed(0)}% of this signal's analog matches are delisted securities that no longer trade`}
                        >
                          {stock.fraction_delisted.toFixed(0)}% Delisted
                        </Badge>
                      )}
                    </h3>
                    {stock.query.ticker_name && (
                      <div
                        className="small text-muted"
                        style={isObfuscated ? {filter: "blur(5px)", userSelect: "none"} : {}}
                      >
                        {stock.query.ticker_name}
                      </div>
                    )}
                    <div className="text-muted mt-1">
                      Pattern Period: {formatDate(stock.query.query_dates[0], 0)} - {formatDate(stock.query.query_dates[stock.query.query_dates.length - 1], 0)}
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="confidence-score">
                      <div className="d-flex align-items-center">
                        <small className="text-muted me-2">Score</small>
                        <Badge
                          color={stock.direction === "up" ? "primary" : "danger"}
                          pill
                        >
                          {stock.direction === "up"
                            ? stock.total_up_score.toFixed(1)
                            : stock.total_down_score.toFixed(1)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="p-3">
                  <div className="mini-chart-container mb-3" style={{height: "100px"}}>
                    <Line data={createChartData(stock)} options={miniChartOptions} />
                  </div>

                  <div className="match-stats">
                    <div className="d-flex justify-content-between mb-2">
                      <div>
                        <small className="text-muted d-block">Probability</small>
                        <span
                          className={
                            stock.direction === "up" ? "text-success" : "text-danger"
                          }
                        >
                          {stock.direction === "up"
                            ? `${stock.fraction_up.toFixed(1)}% UP`
                            : `${stock.fraction_dn.toFixed(1)}% DOWN`}
                        </span>
                      </div>
                      <div className="text-end">
                        <small className="text-muted d-block">Average Move</small>
                        <span
                          className={
                            stock.direction === "up" ? "text-success" : "text-danger"
                          }
                        >
                          {stock.direction === "up"
                            ? `+${stock.avg_up.toFixed(1)}%`
                            : `${stock.avg_dn.toFixed(1)}%`}
                        </span>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between mt-3">
                      <div>
                        <small className="text-muted d-block">Matches</small>
                        <div className="d-flex">
                          <div className="me-2">
                            <span className="text-success">{stock.count_up} Up</span>
                          </div>
                          <div>
                            <span className="text-danger">{stock.count_dn} Down</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-end">
                        <small className="text-muted d-block">Max Move</small>
                        <div className="d-flex justify-content-end">
                          <div className="me-2">
                            <span className="text-success">
                              +{stock.max_up.toFixed(1)}%
                            </span>
                          </div>
                          <div>
                            <span className="text-danger">
                              {stock.max_dn.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isObfuscated ? (
                    <Button
                      color="primary"
                      className="w-100 mt-3"
                      href={authUrlFor(SIGNUP_URL)}
                    >
                      🔒 Sign up to reveal
                    </Button>
                  ) : (
                    <div className="d-flex mt-3">
                      <Button
                        color="primary"
                        outline
                        className="flex-grow-1"
                        href={buildViewFeaturedUrl({ direction: stock.direction!, ticker: stock.query.ticker, window: stock.window!, date: selectedDate })}
                        rel="nofollow"
                        onClick={() => captureMatchClick(stock)}
                      >
                        View Analysis
                      </Button>
                      <ShareButton
                        color="primary"
                        outline
                        className="ms-2"
                        title={`${stock.query.ticker} — DejaChart`}
                        path={`/api/share/featured/${stock.query.ticker}?window=${stock.window}&direction=${stock.direction}${content?.featured_as_of ? `&date=${content.featured_as_of}` : ""}`}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  // Table header component
  const TableHeader = () => {
    return (
      <tr>
        <th>Ticker</th>
        <th>Direction</th>
        <th>Horizon</th>
        <th># Up</th>
        <th># Down</th>
        <th>% Up</th>
        <th>% Down</th>
        <th>Avg Up</th>
        <th>Avg Down</th>
        <th>Max Up</th>
        <th>Max Down</th>
        {showScoreColumns && (
          <>
            <th>Up Score</th>
            <th>Down Score</th>
          </>
        )}
        <th>Chart</th>
      </tr>
    );
  };

  // Table view for stock data
  const StockTableView = ({stocks}: {stocks: StockData[]}) => {
    if (stocks.length === 0) {
      return (
        <div className="text-center py-4">
          <p>No matches found for the selected filters.</p>
        </div>
      );
    }

    return (
      <>
        <div className="d-flex justify-content-end mb-2">
          <Button
            color="link"
            size="sm"
            className="p-0 text-muted"
            onClick={() => setShowScoreColumns((v) => !v)}
          >
            {showScoreColumns ? "Hide score columns" : "Show score columns"}
          </Button>
        </div>
        <Table responsive className="table-striped border">
          <thead>
            <TableHeader />
          </thead>
          <tbody>
            {stocks.map((stock, i) => {
              const isObfuscated = stock.query.ticker === "?????";
              return (
                <tr key={`${stock.query.ticker}_${i}_${stock.direction}_${stock.window}`}>
                  <td>
                    {isObfuscated ? (
                      <a href={authUrlFor(SIGNUP_URL)} className="fw-bold text-primary text-decoration-none">
                        <span style={{filter: "blur(5px)", userSelect: "none"}}>{stock.query.ticker}</span>
                        {" "}🔒
                      </a>
                    ) : (
                      <a
                        href={buildViewFeaturedUrl({ direction: stock.direction!, ticker: stock.query.ticker, window: stock.window!, date: selectedDate })}
                        rel="nofollow"
                        className="fw-bold text-primary text-decoration-none"
                        onClick={() => captureMatchClick(stock)}
                      >
                        {stock.query.ticker}
                      </a>
                    )}
                    {stock.query.ticker_name && (
                      <div
                        className="small text-muted"
                        style={isObfuscated ? {filter: "blur(5px)", userSelect: "none"} : {}}
                      >
                        {stock.query.ticker_name}
                      </div>
                    )}
                    <div className="small text-muted">
                      {formatDate(stock.query.query_dates[0], 0)} - {formatDate(stock.query.query_dates[stock.query.query_dates.length - 1], 0)}
                    </div>
                  </td>
                  <td>
                    <Badge color={stock.direction === "up" ? "success" : "danger"}>
                      {stock.direction === "up" ? "BULLISH" : "BEARISH"}
                    </Badge>
                    {stock.ma_pending && (
                      <Badge
                        color="info"
                        className="ms-1"
                        title="This stock is currently under a live merger/acquisition agreement — its price may be converging toward a fixed deal price (arbitrage) rather than moving on the pattern shown here"
                      >
                        M&A
                      </Badge>
                    )}
                    {stock.fraction_delisted > DELISTED_BADGE_THRESHOLD && (
                      <Badge
                        color="warning"
                        className="ms-1"
                        title={`${stock.fraction_delisted.toFixed(0)}% of this signal's analog matches are delisted securities that no longer trade`}
                      >
                        {stock.fraction_delisted.toFixed(0)}% Delisted
                      </Badge>
                    )}
                  </td>
                  <td>
                    <Badge color="secondary">
                      {stock.window} Days
                    </Badge>
                  </td>
                  <td className="text-success">{stock.count_up}</td>
                  <td className="text-danger">{stock.count_dn}</td>
                  <td className="text-success">{stock.fraction_up.toFixed(1)}%</td>
                  <td className="text-danger">{stock.fraction_dn.toFixed(1)}%</td>
                  <td className="text-success">+{stock.avg_up.toFixed(1)}%</td>
                  <td className="text-danger">{stock.avg_dn.toFixed(1)}%</td>
                  <td className="text-success">+{stock.max_up.toFixed(1)}%</td>
                  <td className="text-danger">{stock.max_dn.toFixed(1)}%</td>
                  {showScoreColumns && (
                    <>
                      <td className="fw-bold text-primary">
                        {stock.total_up_score.toFixed(2)}
                      </td>
                      <td className="fw-bold text-danger">
                        {stock.total_down_score.toFixed(2)}
                      </td>
                    </>
                  )}
                  <td>
                    <div style={{width: "80px", height: "30px"}}>
                      <Line data={createChartData(stock)} options={miniChartOptions} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </>
    );
  };

  if (isLoading) {
    return (
      <Container className="py-4">
        <h1 className="page-title mb-2">Top Matches</h1>
        <Row>
          {[1, 2, 3].map((i) => (
            <Col key={i} lg={4} md={6} className="mb-4">
              <CardSkeleton />
            </Col>
          ))}
        </Row>
      </Container>
    );
  }

  const filteredStocks = getFilteredStocks();

  return (
    <Container className="py-4">
      <h1 className="page-title mb-2">Top Matches</h1>
      <p className="text-muted mb-3">
        Stocks whose recent price action most closely matches historical patterns across all tickers.
        Each match is scored by how often similar setups led to upward or downward moves, helping you
        spot high-conviction opportunities.
      </p>
      <DisplayDate />
      {content?.is_teased && (
        <Alert color="info" className="d-flex flex-wrap align-items-center justify-content-between">
          <span>
            👀 Showing today&apos;s matches across every window. Tickers past the top few are
            hidden until you sign up — a <strong>free</strong> account reveals all of them.
          </span>
          <Button color="primary" size="sm" href="/signup" className="mt-2 mt-md-0">
            Create a free account →
          </Button>
        </Alert>
      )}
      <FilterControls />

      <h5 className="results-header mb-3">
        {filteredStocks.length} Matches Found
        {directionFilter !== "all" &&
          ` • ${directionFilter === "up" ? "Bullish" : "Bearish"}`}
        {` • ${windowFilter} Day Horizon`}
        {tickerTypeFilter !== "all" &&
          ` • ${tickerTypeFilter === "stock" ? "Stocks/ETFs" : tickerTypeFilter === "index" ? "Indexes" : tickerTypeFilter === "futures" ? "Futures" : "Crypto & Forex"}`}
      </h5>
      {viewMode === "cards" ? (
        <StockCardView stocks={filteredStocks} />
      ) : (
        <StockTableView stocks={filteredStocks} />
      )}
    </Container>
  );
};

export default Featured;
